import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Ear,
  LayoutDashboard,
  Users,
  UserCheck,
  Package,
  MessageCircle,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  Bell,
  ClipboardList,
  Mail,
  Clock,
  FileBarChart,
  Tent,
  Upload,
  UserCog,
  Plug,
  Bot,
  FileText,
  Zap,
  ListPlus,
  Sparkles,
  Radio
} from "lucide-react";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/leads", icon: Users, label: "Leads" },
  { path: "/camps", icon: Tent, label: "Camps" },
  { path: "/customers", icon: UserCheck, label: "Customers" },
  { path: "/tasks", icon: ClipboardList, label: "Tasks" },
  { path: "/follow-ups", icon: Clock, label: "Follow-ups" },
  { path: "/messages", icon: Mail, label: "Messages" },
  { path: "/ai-agent", icon: Sparkles, label: "AI Agents" },
  { path: "/eligible-leads", icon: Users, label: "Eligible Leads" },
  { path: "/whatsapp-monitor", icon: Radio, label: "Live Monitor" },
  { path: "/ai-automation", icon: Zap, label: "AI Automation", adminOnly: true },
  { path: "/queue-management", icon: ListPlus, label: "Queue Console", adminOnly: true },
  { path: "/ai-followup", icon: Bot, label: "AI Follow-up", adminOnly: true },
  { path: "/huskyvoice-docs", icon: FileText, label: "HuskyVoice Docs", adminOnly: true },
  { path: "/products", icon: Package, label: "Products" },
  { path: "/whatsapp", icon: MessageCircle, label: "WhatsApp" },
  { path: "/appointments", icon: Calendar, label: "Appointments" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/crm-reports", icon: FileBarChart, label: "CRM Reports" },
  { path: "/integrations", icon: Plug, label: "Integrations", adminOnly: true },
  { path: "/import", icon: Upload, label: "Import Data" },
  { path: "/users", icon: UserCog, label: "User Management", adminOnly: true },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export const DashboardLayout = ({ children, user, logout }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_heartech-dash/artifacts/7jmxr97o_hearclear%20logo.png" 
              alt="HearClear" 
              className="h-10 w-auto"
            />
            <span className="font-bold text-lg text-cyan-900 font-[Manrope]">CLM</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems
            .filter((item) => !item.adminOnly || (user && ['admin', 'super_admin'].includes(user.role)))
            .map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-9 h-9">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-cyan-100 text-cyan-700">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 font-[Manrope]">
              {navItems.find(item => window.location.pathname === item.path)?.label || "Dashboard"}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5 text-slate-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-cyan-100 text-cyan-700 text-sm">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="w-4 h-4 text-slate-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
